import React, { useState, useCallback } from 'react';
import {
  Upload,
  FileJson,
  AlertCircle,
  CheckCircle,
  XCircle,
  Download,
  RefreshCw,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Layers
} from 'lucide-react';
import axios from 'axios';
import { API_ORIGIN } from '../config/api';
import { useLanguage } from '../i18n/LanguageContext';

interface ImportStats {
  imported: number;
  skipped?: number;
  categories: string[];
}

interface ValidationResult {
  valid: boolean;
  format: string;
  documentCount: number;
  categories: string[];
  issues: string[];
}

interface KnowledgeImporterProps {
  onImportComplete?: (stats: ImportStats) => void;
  onClose?: () => void;
}

export const KnowledgeImporter: React.FC<KnowledgeImporterProps> = ({
  onImportComplete,
  onClose
}) => {
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [importMode, setImportMode] = useState<'replace' | 'merge'>('merge');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<ImportStats | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const API_BASE_URL = API_ORIGIN;

  // 处理文件拖拽
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  // 处理文件放置
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.type === 'application/json' || droppedFile.name.endsWith('.json'))) {
      handleFileSelect(droppedFile);
    } else {
      setError(t('请上传 JSON 文件', 'Please upload a JSON file'));
    }
  }, []);

  // 处理文件选择
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  }, []);

  // 处理文件选择
  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setValidationResult(null);
    setError(null);
    setSuccess(null);
    validateFile(selectedFile);
  };

  // 验证文件
  const validateFile = async (fileToValidate: File) => {
    setValidating(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', fileToValidate);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/knowledge/import/validate`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      setValidationResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || t('文件验证失败', 'File validation failed'));
    } finally {
      setValidating(false);
    }
  };

  // 执行导入
  const handleImport = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = importMode === 'replace' 
        ? `${API_BASE_URL}/api/knowledge/import`
        : `${API_BASE_URL}/api/knowledge/import/merge`;

      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(response.data.stats);
      setFile(null);
      setValidationResult(null);

      if (onImportComplete) {
        onImportComplete(response.data.stats);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('导入失败', 'Import failed'));
    } finally {
      setUploading(false);
    }
  };

  // 取消导入
  const handleCancel = () => {
    setFile(null);
    setValidationResult(null);
    setError(null);
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* 头部 */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-indigo-600" />
            <h3 className="font-medium text-gray-900 dark:text-white">{t('导入知识库', 'Import Knowledge Base')}</h3>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <XCircle className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* 内容 */}
      <div className="p-6">
        {/* 拖拽上传区域 */}
        {!file && !success && (
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="import-file"
              className="hidden"
              onChange={handleFileInput}
              accept=".json"
            />
            
            <FileJson className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              {t('拖拽 JSON 文件到这里，或', 'Drag a JSON file here, or')}
              <label
                htmlFor="import-file"
                className="text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer ml-1"
              >
                {t('浏览文件', 'browse files')}
              </label>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('支持从知识库导出的 JSON 文件', 'Supports JSON files exported from the knowledge base')}
            </p>
          </div>
        )}

        {/* 文件验证中 */}
        {validating && (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">{t('正在验证文件...', 'Validating file...')}</p>
          </div>
        )}

        {/* 验证结果 */}
        {validationResult && !success && (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${
              validationResult.valid
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
            }`}>
              <div className="flex items-start gap-3">
                {validationResult.valid ? (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${
                    validationResult.valid
                      ? 'text-green-800 dark:text-green-300'
                      : 'text-yellow-800 dark:text-yellow-300'
                  }`}>
                    {validationResult.valid ? t('文件验证通过', 'File validated') : t('文件格式有问题', 'File format has issues')}
                  </p>
                  <div className="mt-2 text-sm space-y-1">
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('格式', 'Format')}: {validationResult.format === 'knowledge-base' ? t('知识库导出', 'Knowledge base export') : t('备份文件', 'Backup file')}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('文档数量', 'Document count')}: {validationResult.documentCount}
                    </p>
                    {validationResult.categories.length > 0 && (
                      <p className="text-gray-600 dark:text-gray-400">
                        {t('包含分类', 'Categories')}: {validationResult.categories.join(', ')}
                      </p>
                    )}
                    {validationResult.issues.length > 0 && (
                      <div className="mt-2">
                        <p className="text-yellow-700 dark:text-yellow-500 font-medium">{t('发现的问题', 'Issues found')}:</p>
                        <ul className="list-disc list-inside text-sm text-yellow-600 dark:text-yellow-400">
                          {validationResult.issues.map((issue, index) => (
                            <li key={index}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 高级选项 */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full px-4 py-3 flex items-center justify-between text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="font-medium">{t('高级导入选项', 'Advanced import options')}</span>
                {showAdvanced ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        checked={importMode === 'merge'}
                        onChange={() => setImportMode('merge')}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{t('合并导入', 'Merge import')}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('保留现有文档，只添加不重复的新文档', 'Keep existing documents and only add new non-duplicate documents')}
                        </p>
                      </div>
                    </label>
                    
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        checked={importMode === 'replace'}
                        onChange={() => setImportMode('replace')}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{t('替换导入', 'Replace import')}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('清空当前知识库，用导入的文档替换', 'Clear the current knowledge base and replace it with imported documents')}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* 操作按钮 */}
            <div className="flex gap-3">
              <button
                onClick={handleImport}
                disabled={uploading || !validationResult.valid}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {t('导入中...', 'Importing...')}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {t('确认导入', 'Confirm import')}
                  </>
                )}
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('取消', 'Cancel')}
              </button>
            </div>
          </div>
        )}

        {/* 导入成功 */}
        {success && (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-300">
                  {t('导入成功！', 'Import successful!')}
                </p>
                <div className="mt-2 text-sm space-y-1">
                  <p className="text-green-700 dark:text-green-400">
                    {t(`成功导入 ${success.imported} 个文档`, `Imported ${success.imported} document(s) successfully`)}
                  </p>
                  {success.skipped !== undefined && success.skipped > 0 && (
                    <p className="text-yellow-600 dark:text-yellow-500">
                      {t(`跳过 ${success.skipped} 个重复文档`, `Skipped ${success.skipped} duplicate document(s)`)}
                    </p>
                  )}
                  <p className="text-green-700 dark:text-green-400">
                    {t('包含分类', 'Categories')}: {success.categories.join(', ')}
                  </p>
                </div>
                <button
                  onClick={handleCancel}
                  className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {t('完成', 'Done')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 错误提示 */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800 dark:text-red-300">{t('导入失败', 'Import failed')}</p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
